import React from 'react';
import { Link } from 'react-router-dom';
import type { HomepageHeatItem } from '../../../apiClient';
import OfficialAvatar from '../OfficialAvatar';

function timeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function scoreTone(score: number | null): string {
  if (score == null) return 'text-cream-300';
  if (score < 40) return 'text-red-400';
  if (score < 70) return 'text-gold-400';
  return 'text-emerald-300';
}

const RecentMovementCard: React.FC<{ item: HomepageHeatItem }> = ({ item }) => {
  return (
    <article className="rounded-2xl border border-white/10 bg-white/5 p-4 sm:p-5 backdrop-blur-sm">
      <div className="flex items-start gap-3 mb-4">
        <OfficialAvatar
          name={item.name}
          photoUrl={item.photoUrl}
          className="w-14 h-14 rounded-xl bg-white/10 border border-white/10 flex items-center justify-center shrink-0"
          imgClassName="w-14 h-14 rounded-xl object-cover border border-white/10 shrink-0"
          initialsClassName="text-sm font-black text-cream-100 uppercase"
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-1.5">
            <span className="rounded-full border border-gold-500/30 bg-gold-500/10 px-2 py-1 text-[9px] font-black uppercase tracking-[0.22em] text-gold-400">
              {item.eventLabel}
            </span>
            <span className="text-[10px] font-black uppercase tracking-widest text-cream-300/70">
              {timeAgo(item.eventTimestamp)}
            </span>
          </div>
          <h3 className="text-base font-black uppercase tracking-tight text-cream-50 leading-tight">
            {item.name}
          </h3>
          <p className="text-[10px] font-black uppercase tracking-widest text-cream-300/70 mt-1">
            {item.officeName}{item.state ? ` — ${item.state}` : ''}
          </p>
        </div>
      </div>

      <p className="text-sm font-bold text-cream-50 mb-2 leading-relaxed">
        {item.headline}
      </p>
      <p className="text-sm text-cream-200/80 leading-relaxed mb-4 min-h-[3rem]">
        {item.detail}
      </p>

      <div className="grid grid-cols-2 gap-2.5 mb-4">
        <div className="rounded-xl border border-white/10 bg-black/10 p-3">
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-cream-300/70 mb-1">Spine score</p>
          <p className={`text-sm font-black uppercase tracking-tight ${scoreTone(item.accountabilityScore)}`}>
            {item.accountabilityScore == null ? 'Not enough record' : `${item.accountabilityScore}/100`}
          </p>
        </div>
        <div className="rounded-xl border border-white/10 bg-black/10 p-3">
          <p className="text-[9px] font-black uppercase tracking-[0.2em] text-cream-300/70 mb-1">Adoptions</p>
          <p className="text-sm font-black uppercase tracking-tight text-cream-50">
            {item.adoptionCount}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-white/10">
        <p className="text-[10px] font-black uppercase tracking-widest text-cream-300/60">
          Updated {new Date(item.eventTimestamp).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
        </p>
        <Link
          to={item.sourcePath}
          className="inline-flex items-center justify-center rounded-xl bg-gold-500 hover:bg-gold-400 text-navy-950 px-4 py-2 text-[10px] font-black uppercase tracking-widest transition shrink-0"
        >
          {item.sourceLabel}
        </Link>
      </div>
    </article>
  );
};

export default RecentMovementCard;
